import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@reach/disclosure";
import APILink from "components/ui/APILink";
import { NoPrint } from "components/ui/PrintHelpers";
import styled from "styled-components";
import OutboundLink from "components/ui/OutboundLink";
import { LargeSpacer } from "components/ui/VerticalSpacers";
import ucBerkeleyLogo from "assets/images/uc_berkeley_logo.svg";
import ucBerkeleyLogoSmall from "assets/images/uc_berkeley_logo_small.svg";
import { getServerInfo } from "data/slices/serverInfo";
import { getPublicLinks } from "data/actions/getPublicLinks";
import { useCSRFToken } from "components/hooks/useCSRFToken";
import { useSlice } from "components/hooks/useSlice";
export function Footer() {
  const dispatch = useDispatch();
  const csrfToken = useCSRFToken();

  useEffect(() => {
    if (csrfToken) {
      dispatch(getPublicLinks());
    }
  }, [dispatch, csrfToken]);

  // Making the ReachUI a controlled component allows deferring data loading
  // of the server info until the disclosure is shown.
  const [showServerInfo, setShowServerInfo] = useState(false);

  const { aboutLink, supportLink, usagePolicyLink } = useSelector((state) => {
    const {
      publicLinks: {
        UC_CC_CLC_SPLASH_ABOUT: aboutLink,
        UC_CC_CLC_SPLASH_SUPPORT: supportLink,
        UC_CC_CLC_SPLASH_USAGE_POLICY: usagePolicyLink,
      } = {},
    } = state;

    return {
      aboutLink,
      supportLink,
      usagePolicyLink,
    };
  });

  const { actingAsUid, delegateActingAsUid, advisorActingAsUid } =
    useSlice("myStatus");

  const isActingAs = actingAsUid || delegateActingAsUid || advisorActingAsUid;

  return (
    <>
      <LargeSpacer />
      <FooterStyles $isActingAs={isActingAs}>
        <Disclosure
          open={showServerInfo}
          onChange={() => setShowServerInfo(!showServerInfo)}
        >
          <footer>
            <div className="mainFooter">
              <div className="logoCopyright">
                <DisclosureButton
                  style={{
                    background: `transparent`,
                    border: `none`,
                    cursor: `pointer`,
                    padding: 0,
                  }}
                >
                  <img
                    src={ucBerkeleyLogo}
                    alt="UC Berkeley Logo"
                    className="logo"
                  />
                  <img
                    src={ucBerkeleyLogoSmall}
                    alt="UC Berkeley Logo"
                    className="logoSmall"
                  />
                </DisclosureButton>
                &copy; {new Date().getFullYear()} UC Regents
              </div>
              <div className="links">
                <NoPrint>
                  <APILink link={usagePolicyLink} />
                  <APILink link={aboutLink} />
                  <APILink link={supportLink} />
                </NoPrint>
              </div>
            </div>

            <DisclosurePanel>
              {showServerInfo && <ServerInfo />}
            </DisclosurePanel>
          </footer>
        </Disclosure>
      </FooterStyles>
    </>
  );
}
Footer.displayName = "Footer";

function ServerInfo() {
  const {
    loadState,
    gitCommit,
    hostname,
    uid,
    firstVisited,
    actingAsUid,
    delegateActingAsUid,
    advisorActingAsUid,
    versions: { api, application, currentDbSchema } = {},
  } = useSelector(({ serverInfo, myStatus }) => {
    const { uid, actingAsUid, delegateActingAsUid, advisorActingAsUid } =
      myStatus;

    return {
      ...serverInfo,
      uid,
      actingAsUid,
      delegateActingAsUid,
      advisorActingAsUid,
    };
  });

  const gitHubHref = `https://github.com/sis-berkeley-edu/cal-central/commit/${gitCommit}`;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getServerInfo());
  }, [dispatch]);

  if (loadState === "success") {
    return (
      <>
        <LargeSpacer />
        <ul>
          <li>Hostname: {hostname}</li>
          {uid && <li>UID: {uid}</li>}
          {actingAsUid && <li>Actually logged in as: {actingAsUid}</li>}
          {delegateActingAsUid && <li>Delegate user: {delegateActingAsUid}</li>}
          {advisorActingAsUid && <li>Advisor user: {advisorActingAsUid}</li>}
          <li>Application Version: {application}</li>
          <li>API Version: {api}</li>
          <li>Current DB Schema: {currentDbSchema}</li>
          <li>Up Since: {firstVisited}</li>
          <li>
            Git commit:{" "}
            <OutboundLink href={gitHubHref}>
              {gitCommit.slice(0, 7)}
            </OutboundLink>
          </li>
        </ul>
      </>
    );
  }

  return null;
}

const FooterStyles = styled.div`
  @media screen and (max-width: 767px) {
    footer {
      background-color: var(--mako);
      color: var(--white);
      padding: var(--lg);

      a {
        color: var(--regent-st-blue);
      }

      .logoCopyright {
        display: flex;
        justify-content: space-between;
      }

      .logo {
        display: none;
      }

      .links {
        margin-top: var(--lg);
        display: flex;
        gap: 5px;
      }

      .links a {
        border-left: 1px solid var(--link-color);
        font-weight: bold;
        padding-left: 7px;
        padding-right: 7px;
      }

      .links a:first-child {
        border-left: 0;
      }

      ${({ $isActingAs }) => $isActingAs && `margin-bottom: 76px;`}
    }
  }

  @media screen and (min-width: 768px) {
    background-color: var(--wild-sand);
    color: var(--base-color);

    .mainFooter {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;

      .logoSmall {
        display: none;
      }

      .logoCopyright {
        .logo {
          margin-right: 20px;
          vertical-align: middle;
        }
      }

      .links {
        display: flex;
        gap: 5px;
      }

      .links a {
        border-left: 1px solid var(--link-color);
        font-weight: bold;
        padding-left: 7px;
        padding-right: 7px;
      }

      .links a:first-child {
        border-left: 0;
      }
    }

    footer {
      border-top: 1px solid #ddd;
      margin: 0 auto;
      max-width: var(--max-width);
      padding: var(--lg);

      ${({ $isActingAs }) => $isActingAs && `margin-bottom: 50px;`}
    }
  }
`;
